<template>
  <div class="column">
    <woot-modal-header :header-title="modalTitle" />

    <div class="steps-indicator">
      <div class="bullet-diver">&nbsp;</div>
      <ul class="bullets">
        <li class="bullet-item">
          <span
            class="bullet"
            :class="{ active: currentStep === 1, completed: currentStep > 1 }"
          >
            1
          </span>
        </li>
        <li class="bullet-item">
          <span
            class="bullet"
            :class="{ active: currentStep === 2, completed: currentStep > 2 }"
          >
            2
          </span>
        </li>
        <li class="bullet-item">
          <span
            class="bullet"
            :class="{ active: currentStep === 3, completed: currentStep > 3 }"
          >
            3
          </span>
        </li>
        <li class="bullet-item">
          <span
            class="bullet"
            :class="{ active: currentStep === 4, completed: currentStep > 4 }"
          >
            4
          </span>
        </li>
      </ul>
    </div>

    <div class="row modal-content">
      <div class="medium-12 columns">
        <template v-if="currentStep === 1">
          <woot-input
            v-model="bulkAction.name"
            label="Nome da ação"
            type="text"
            :class="{ error: $v.bulkAction.name.$error }"
            :error="$v.bulkAction.name.$error ? 'Informe um nome' : ''"
            placeholder="Ex: Resolver conversas antigas"
            @blur="$v.bulkAction.name.$touch"
          />

          <section>
            <label>Filtros (AND obrigatório)</label>
            <div class="medium-12 columns filters-wrap">
              <filter-input-box
                v-for="(condition, i) in bulkAction.conditions"
                :key="i"
                v-model="bulkAction.conditions[i]"
                :filter-attributes="getAttributes(bulkAction.event_name)"
                :input-type="getInputType(bulkAction.conditions[i].attribute_key)"
                :operators="getOperators(bulkAction.conditions[i].attribute_key)"
                :dropdown-values="
                  getConditionDropdownValues(bulkAction.conditions[i].attribute_key)
                "
                :show-query-operator="false"
                :custom-attribute-type="
                  getCustomAttributeType(bulkAction.conditions[i].attribute_key)
                "
                :v="$v.bulkAction.conditions.$each[i]"
                @resetFilter="resetCondition(i, bulkAction.conditions[i])"
                @removeFilter="removeCondition(i)"
              />

              <div class="filter-actions">
                <woot-button
                  icon="add"
                  color-scheme="success"
                  variant="smooth"
                  size="small"
                  @click="appendNewCondition"
                >
                  Adicionar filtro
                </woot-button>
              </div>
            </div>
          </section>
        </template>

        <template v-if="currentStep === 2">
          <section>
            <label>Ações</label>

            <div class="medium-12 columns filters-wrap">
              <automation-action-input
                v-for="(action, i) in bulkAction.actions"
                :key="i"
                v-model="bulkAction.actions[i]"
                :action-types="bulkActionTypes"
                :dropdown-values="
                  getActionDropdownValues(bulkAction.actions[i].action_name)
                "
                :show-action-input="showActionInput(bulkAction.actions[i].action_name)"
                :v="$v.bulkAction.actions.$each[i]"
                @resetAction="resetAction(i)"
                @removeAction="removeAction(i)"
              />

              <div class="filter-actions">
                <woot-button
                  icon="add"
                  color-scheme="success"
                  variant="smooth"
                  size="small"
                  @click="appendNewAction"
                >
                  Adicionar ação
                </woot-button>
              </div>
            </div>
          </section>
        </template>

        <template v-if="currentStep === 3">
          <div class="preview-wrap">
            <woot-loading-state
              v-if="uiFlags.isPreviewing"
              message="Gerando pré-visualização"
            />

            <template v-else>
              <p class="preview-count">
                Total de conversas impactadas: <strong>{{ previewTotal }}</strong>
              </p>

              <table
                v-if="previewConversations.length"
                class="woot-table"
                aria-describedby="preview-table"
              >
                <thead>
                  <th>ID</th>
                  <th>Contato</th>
                  <th>Status</th>
                  <th>Responsável</th>
                  <th>Inbox</th>
                </thead>
                <tbody>
                  <tr
                    v-for="conversation in previewConversations"
                    :key="conversation.id"
                  >
                    <td>{{ conversation.id }}</td>
                    <td>{{ conversation.contact_name || '-' }}</td>
                    <td>{{ getLabelConversationStatus(conversation.status) }}</td>
                    <td>{{ conversation.assignee_name || '-' }}</td>
                    <td>{{ conversation.inbox_name || '-' }}</td>
                  </tr>
                </tbody>
              </table>

              <p v-else class="no-items-error-message">
                Nenhuma conversa encontrada para os filtros selecionados.
              </p>
            </template>
          </div>
        </template>

        <template v-if="currentStep === 4">
          <div>
            <p>
              Esta ação será agendada para execução fora do horário comercial.
            </p>
            <p>
              Horário previsto: <strong>{{ scheduledAtLabel }}</strong>
            </p>
            <p>
              Conversas no preview: <strong>{{ previewTotal }}</strong>
            </p>
          </div>
        </template>

        <div class="medium-12 columns">
          <div class="modal-footer justify-content-end w-full">
            <woot-button
              v-if="currentStep > 1"
              class="button clear"
              @click.prevent="prevStep"
            >
              Voltar
            </woot-button>

            <woot-button v-if="currentStep < 4" @click="nextStep">
              Próximo
            </woot-button>

            <woot-button
              v-if="currentStep === 4"
              :is-loading="uiFlags.isCreating || uiFlags.isUpdating"
              @click="saveBulkAction"
            >
              {{ submitButtonLabel }}
            </woot-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import automationMethodsMixin from 'dashboard/mixins/automations/methodsMixin';
import { generateAutomationPayload } from 'dashboard/helper/automationHelper';
import filterInputBox from 'dashboard/components/widgets/FilterInput/Index.vue';
import automationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import { AUTOMATIONS } from '../automation/constants';
import { BULK_ACTION_FILTERS } from './constants/filters';
import { BULK_ACTION_TYPES } from './constants/actions';

export default {
  components: {
    filterInputBox,
    automationActionInput,
  },
  mixins: [alertMixin, automationMethodsMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    bulkActionToEdit: {
      type: Object,
      default: null,
    },
  },
  validations: {
    bulkAction: {
      name: {
        required,
      },
      conditions: {
        required,
        $each: {
          values: {
            required: requiredIf(prop => {
              return !(
                prop.filter_operator === 'is_present' ||
                prop.filter_operator === 'is_not_present'
              );
            }),
          },
        },
      },
      actions: {
        required,
        $each: {
          action_params: {
            required: requiredIf(prop => {
              return !(
                prop.action_name === 'resolve_conversation' ||
                prop.action_name === 'unassign_agent' ||
                prop.action_name === 'remove_assigned_team'
              );
            }),
          },
        },
      },
    },
  },
  data() {
    return {
      currentStep: 1,
      automationTypes: {
        conversation_created: {
          ...AUTOMATIONS.conversation_created,
          conditions: BULK_ACTION_FILTERS,
        },
      },
      automationActionTypes: BULK_ACTION_TYPES,
      bulkAction: {
        name: null,
        event_name: 'conversation_created',
        conditions: [
          {
            attribute_key: 'status',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
            custom_attribute_type: '',
          },
        ],
        actions: [
          {
            action_name: 'assign_agent',
            action_params: [],
          },
        ],
      },
      allCustomAttributes: [],
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'bulkActionsSettings/getUIFlags',
      preview: 'bulkActionsSettings/getPreview',
    }),
    automation() {
      return this.bulkAction;
    },
    isEditMode() {
      return Boolean(this.bulkActionToEdit?.id);
    },
    modalTitle() {
      return this.isEditMode ? 'Editar ação massiva' : 'Nova ação massiva';
    },
    submitButtonLabel() {
      return this.isEditMode ? 'Salvar alterações' : 'Confirmar e agendar';
    },
    bulkActionTypes() {
      return this.automationActionTypes;
    },
    previewTotal() {
      return (
        this.preview?.total_count || this.preview?.total || this.preview?.count || 0
      );
    },
    previewConversations() {
      return (
        this.preview?.sample_conversations ||
        this.preview?.sample ||
        this.preview?.conversations ||
        []
      ).slice(0, 5);
    },
    scheduledAtLabel() {
      if (this.bulkAction.scheduled_at) {
        return new Date(this.bulkAction.scheduled_at).toLocaleString();
      }

      const nextMidnight = new Date();
      nextMidnight.setHours(24, 0, 0, 0);
      return nextMidnight.toLocaleString();
    },
  },
  async mounted() {
    await Promise.allSettled([
      this.$store.dispatch('inboxes/get'),
      this.$store.dispatch('agents/get'),
      this.$store.dispatch('contacts/get'),
      this.$store.dispatch('teams/get'),
      this.$store.dispatch('labels/get'),
      this.$store.dispatch('closingReasons/get', { page: 1, limit: 100 }),
      this.$store.dispatch('events/get'),
      this.$store.dispatch('attributes/get'),
    ]);

    this.allCustomAttributes = this.$store.getters['attributes/getAttributes'];
    this.prefillFromRecord();
  },
  watch: {
    bulkActionToEdit: {
      deep: true,
      handler() {
        this.prefillFromRecord();
      },
    },
  },
  methods: {
    getLabelConversationStatus(key) {
      if (!key) return '-';
      return this.$t(`UPCOMING_SCHEDULED_MESSAGES_PAGE.CONVERSATION_STATUS.${key.toUpperCase()}`);
    },
    parseArrayPayload(value) {
      if (Array.isArray(value)) {
        return value;
      }

      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }

      return [];
    },
    mapConditionFallback(condition) {
      const values = condition?.values;
      const normalizedValues = Array.isArray(values)
        ? values
        : values === null || values === undefined || values === ''
          ? []
          : [values];

      return {
        attribute_key: condition?.attribute_key || 'status',
        filter_operator: condition?.filter_operator || 'equal_to',
        values: normalizedValues,
        query_operator: (condition?.query_operator || 'and').toLowerCase(),
        custom_attribute_type: condition?.custom_attribute_type || '',
      };
    },
    mapActionFallback(action) {
      const params = action?.action_params;
      const normalizedParams = Array.isArray(params)
        ? params
        : params === null || params === undefined
          ? []
          : [params];

      return {
        action_name: action?.action_name || 'assign_agent',
        action_params: normalizedParams,
      };
    },
    prefillFromRecord() {
      if (!this.isEditMode) {
        return;
      }

      const rawConditions = this.parseArrayPayload(this.bulkActionToEdit.conditions);
      const rawActions = this.parseArrayPayload(this.bulkActionToEdit.actions);

      const automationToHydrate = {
        event_name: 'conversation_created',
        conditions: rawConditions,
        actions: rawActions,
      };

      let conditions = [];
      let actions = [];

      try {
        conditions = this.manifestConditions(automationToHydrate).map(condition => ({
          ...condition,
          query_operator: (condition.query_operator || 'and').toLowerCase(),
        }));
        actions = this.manifestActions(automationToHydrate);
      } catch (error) {
        conditions = rawConditions.map(this.mapConditionFallback);
        actions = rawActions.map(this.mapActionFallback);
      }

      this.bulkAction = {
        ...this.bulkAction,
        name: this.bulkActionToEdit.name || null,
        scheduled_at: this.bulkActionToEdit.scheduled_at || null,
        conditions: conditions.length
          ? conditions
          : [
              {
                attribute_key: 'status',
                filter_operator: 'equal_to',
                values: '',
                query_operator: 'and',
                custom_attribute_type: '',
              },
            ],
        actions: actions.length
          ? actions
          : [
              {
                action_name: 'assign_agent',
                action_params: [],
              },
            ],
      };
    },
    appendNewCondition() {
      this.bulkAction.conditions.push({
        attribute_key: 'status',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        custom_attribute_type: '',
      });
    },
    appendNewAction() {
      this.bulkAction.actions.push({
        action_name: 'assign_agent',
        action_params: [],
      });
    },
    removeCondition(index) {
      if (this.bulkAction.conditions.length <= 1) {
        this.showAlert('É necessário pelo menos um filtro.');
        return;
      }
      this.bulkAction.conditions.splice(index, 1);
    },
    removeAction(index) {
      if (this.bulkAction.actions.length <= 1) {
        this.showAlert('É necessário pelo menos uma ação.');
        return;
      }
      this.bulkAction.actions.splice(index, 1);
    },
    resetCondition(index, condition) {
      const filter = this.automationTypes.conversation_created.conditions.find(
        item => item.key === condition.attribute_key
      );

      this.bulkAction.conditions[index].filter_operator =
        filter?.filterOperators?.[0]?.value || 'equal_to';
      this.bulkAction.conditions[index].values = '';
      this.bulkAction.conditions[index].query_operator = 'and';
    },
    normalizeConditions() {
      return this.bulkAction.conditions.map(condition => {
        return {
          ...condition,
          query_operator: 'and',
        };
      });
    },
    payloadForApi() {
      const nextMidnight = new Date();
      nextMidnight.setHours(24, 0, 0, 0);

      const payload = generateAutomationPayload({
        conditions: this.normalizeConditions(),
        actions: this.bulkAction.actions,
      });

      return {
        name: this.bulkAction.name,
        conditions: payload.conditions,
        actions: payload.actions,
        scheduled_at: this.bulkAction.scheduled_at || nextMidnight.toISOString(),
        status: 'scheduled',
      };
    },
    async generatePreview() {
      this.$v.bulkAction.conditions.$touch();
      this.$v.bulkAction.actions.$touch();

      if (
        this.$v.bulkAction.conditions.$invalid ||
        this.$v.bulkAction.actions.$invalid
      ) {
        return false;
      }

      try {
        await this.$store.dispatch(
          'bulkActionsSettings/preview',
          this.payloadForApi()
        );
        return true;
      } catch (error) {
        this.showAlert('Não foi possível gerar o preview.');
        return false;
      }
    },
    async nextStep() {
      if (this.currentStep === 1) {
        this.$v.bulkAction.name.$touch();
        this.$v.bulkAction.conditions.$touch();
        if (this.$v.bulkAction.name.$invalid || this.$v.bulkAction.conditions.$invalid) {
          return;
        }
      }

      if (this.currentStep === 2) {
        this.$v.bulkAction.actions.$touch();
        if (this.$v.bulkAction.actions.$invalid) {
          return;
        }
        const previewReady = await this.generatePreview();
        if (!previewReady) {
          return;
        }
      }

      if (this.currentStep < 4) {
        this.currentStep += 1;
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
      }
    },
    async saveBulkAction() {
      try {
        const payload = this.payloadForApi();

        if (this.isEditMode) {
          const updated = await this.$store.dispatch('bulkActionsSettings/update', {
            id: this.bulkActionToEdit.id,
            payload,
          });

          if (updated) {
            this.showAlert('Ação massiva atualizada com sucesso.');
            this.$emit('updated', updated);
            this.onClose();
          }

          return;
        }

        const created = await this.$store.dispatch('bulkActionsSettings/create', payload);
        if (created) {
          this.showAlert('Ação massiva agendada com sucesso.');
          this.$emit('created', created);
          this.onClose();
        }
      } catch (error) {
        this.showAlert(
          this.isEditMode
            ? 'Não foi possível atualizar a ação massiva.'
            : 'Não foi possível agendar a ação massiva.'
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.steps-indicator {
  margin: 0 10% 20px;
  position: relative;
  .bullets {
    list-style: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0;
    .bullet-item {
      z-index: 1;
      .bullet {
        width: 35px;
        height: 35px;
        border-radius: 50%;
        background: #fff;
        border: 1px solid #1b387c;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        color: #1b387c;
        &.active {
          background: #1b387c;
          color: #fff;
        }
        &.completed {
          background: #27ae60;
          border: 1px solid #27ae60;
          color: #fff;
        }
      }
    }
  }
  .bullet-diver {
    width: 100%;
    height: 1px;
    background: #1b387c;
    margin-top: 17px;
    position: absolute;
    top: 0;
  }
}

.preview-wrap {
  margin-top: var(--space-smaller);
}

.preview-count {
  margin-bottom: var(--space-small);
}
</style>
